import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { 
  Users, 
  PieChart, 
  Truck, 
  Factory, 
  UserPlus, 
  BarChart,
  ArrowRight,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Solutions = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeModule, setActiveModule] = useState('crm');
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const moduleEntries = Object.entries(t.solutions.modules);

  const moduleIcons = {
    crm: <Users className="w-6 h-6" />,
    erp: <PieChart className="w-6 h-6" />,
    tms: <Truck className="w-6 h-6" />,
    mrp: <Factory className="w-6 h-6" />,
    hrms: <UserPlus className="w-6 h-6" />,
    kpis: <BarChart className="w-6 h-6" />
  };

  const nextModule = () => {
    setCurrentModuleIndex((prev) => (prev + 1) % moduleEntries.length);
  };

  const prevModule = () => {
    setCurrentModuleIndex((prev) => (prev - 1 + moduleEntries.length) % moduleEntries.length);
  };

  // Auto-advance carousel every 6 seconds on mobile
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (window.innerWidth < 1024) {
        nextModule();
      }
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="solutions" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.solutions.title}
          </h2>
          <p className="text-xl text-gray-600">
            {t.solutions.subtitle}
          </p>
        </div>

        {/* Mobile Carousel */}
        <div className="block lg:hidden mb-8">
          <div className="relative">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentModuleIndex * 100}%)` }}
              >
                {moduleEntries.map(([key, module]) => (
                  <div key={key} className="w-full flex-shrink-0 px-4">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center mb-4">
                          <span className="flex-shrink-0 mr-4" style={{ color: '#115d7b' }}>
                            {moduleIcons[key as keyof typeof moduleIcons]}
                          </span>
                          <div>
                            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-2" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                              {module.title}
                            </span>
                            <h3 className="text-xl font-bold text-gray-900">
                              {module.fullTitle}
                            </h3>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-4">
                          {module.description}
                        </p>
                        
                        <div className="mb-6">
                          <img
                            src={module.imageSrc}
                            alt={`${module.fullTitle} - ${module.description.substring(0, 100)}`}
                            className="w-full h-48 object-cover rounded-lg shadow-md"
                            loading="lazy"
                            width="800"
                            height="480"
                          />
                        </div>
                        
                        <ul className="space-y-2 mb-6">
                          {module.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <span className="w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0" style={{ backgroundColor: '#e0f2fe' }}>
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#115d7b' }}></span>
                              </span>
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="flex flex-col sm:flex-row gap-3">
                          <a 
                            href={`/module/${key}`}
                            className="flex-1 px-4 py-2 text-white rounded-md transition-colors font-medium text-center flex items-center justify-center"
                            style={{ backgroundColor: '#115d7b' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                          >
                            {t.cta.learnMore}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </a>
                          <a 
                            href="/demo"
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-center"
                          >
                            {t.cta.demo}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button 
              onClick={prevModule} 
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              style={{ color: '#115d7b' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <button 
              onClick={nextModule} 
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              style={{ color: '#115d7b' }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {moduleEntries.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentModuleIndex(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    currentModuleIndex === index ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  style={currentModuleIndex === index ? { backgroundColor: '#115d7b' } : {}}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Tablet Layout - Simple Grid */}
        <div className="hidden md:block lg:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {moduleEntries.map(([key, module]) => (
              <div key={key} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <span className="flex-shrink-0 mr-4" style={{ color: '#115d7b' }}>
                      {moduleIcons[key as keyof typeof moduleIcons]}
                    </span>
                    <div>
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-2" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                        {module.title}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900">
                        {module.fullTitle}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    {module.description}
                  </p>
                  
                  <div className="mb-6">
                    <img
                      src={module.imageSrc}
                      alt={`${module.fullTitle} - ${module.description.substring(0, 100)}`}
                      className="w-full h-48 object-cover rounded-lg shadow-md"
                      loading="lazy"
                      width="800"
                      height="480"
                    />
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {module.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0" style={{ backgroundColor: '#e0f2fe' }}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#115d7b' }}></span>
                        </span>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a 
                      href={`/module/${key}`}
                      className="flex-1 px-4 py-2 text-white rounded-md transition-colors font-medium text-center flex items-center justify-center"
                      style={{ backgroundColor: '#115d7b' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                    >
                      {t.cta.learnMore}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                    <a 
                      href="/demo"
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-center"
                    >
                      {t.cta.demo}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex gap-8">
          <div className="w-full lg:w-1/3">
            <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-8">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {t.solutions.modulesTitle}
                </h3>
                <p className="text-gray-500 text-sm">
                  {t.solutions.modulesSubtitle}
                </p>
              </div>
              <div className="divide-y divide-gray-100">
                {moduleEntries.map(([key, module]) => (
                  <button
                    key={key}
                    className={`w-full px-6 py-4 flex items-center text-left hover:bg-gray-50 transition-colors ${
                      activeModule === key ? 'bg-blue-50 border-l-4' : 'border-l-4 border-transparent'
                    }`}
                    style={activeModule === key ? { borderLeftColor: '#115d7b' } : {}}
                    onClick={() => setActiveModule(key)}
                  >
                    <span className={`flex-shrink-0 mr-4 ${activeModule === key ? '' : 'text-gray-400'}`} style={activeModule === key ? { color: '#115d7b' } : {}}>
                      {moduleIcons[key as keyof typeof moduleIcons]}
                    </span>
                    <div>
                      <h4 className={`font-medium ${activeModule === key ? '' : 'text-gray-900'}`} style={activeModule === key ? { color: '#115d7b' } : {}}>
                        {module.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {module.shortDescription}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full lg:w-2/3">
            <div className="bg-white rounded-xl shadow-md h-full p-6 md:p-8">
              {moduleEntries.map(([key, module]) => (
                <div 
                  key={key}
                  className={`h-full ${activeModule === key ? 'block' : 'hidden'}`}
                >
                  <div className="mb-6">
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-2" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
                      {module.title}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {module.fullTitle}
                    </h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <p className="text-gray-600 mb-6">
                        {module.description}
                      </p>
                      <ul className="space-y-3">
                        {module.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <span className="w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0" style={{ backgroundColor: '#e0f2fe' }}>
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#115d7b' }}></span>
                            </span>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="relative rounded-lg overflow-hidden shadow-lg">
                      <img
                        src={module.imageSrc}
                        alt={`${module.fullTitle} - ${module.description.substring(0, 100)}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        width="800"
                        height="600"
                      />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top right, rgba(17, 93, 123, 0.3), transparent)' }} aria-hidden="true"></div>
                    </div>
                  </div>
                  
                  <div className="mt-auto flex flex-col sm:flex-row gap-4">
                    <a 
                      href={`/module/${key}`}
                      className="px-6 py-3 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                      style={{ backgroundColor: '#115d7b' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#0f5269'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#115d7b'}
                    >
                      {t.cta.learnMore}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                    <a 
                      href="/demo"
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-center"
                    >
                      {t.cta.demo}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;